// MBA Lab — Leadership Practice Snapshot UI. Imports only lib/core.js and
// lib/snapshot-model.js, matching team-charter.js's standalone-bundle
// precedent. Event delegation over data-snapshot-item means the 27 radio
// groups never need individually-wired listeners.
import { store, trackToolAction } from './lib/core.js';
import { hydrate, mountSyncIndicator, syncedStore } from './lib/account-sync.js';
import {
  SCHEMA_VERSION,
  TOTAL_ITEMS,
  createInitialState,
  parseSavedState,
  serializeForSave,
  setAnswer,
  countAnswered,
  isComplete,
  buildReadout,
} from './lib/snapshot-model.js';
import { ITEMS, DOMAIN_ORDER } from './lib/snapshot-items.js';
import { initToolDock } from './lib/tool-dock.js';

const TOOL_NAME = 'leadership-practice-snapshot';
const AUTOSAVE_DEBOUNCE_MS = 400;
const ANNOUNCE_DEBOUNCE_MS = 1200;

function byId(id) {
  return document.getElementById(id);
}

function clearChildren(el) {
  while (el.firstChild) el.removeChild(el.firstChild);
}

class LeadershipPracticeSnapshot {
  constructor() {
    this.cacheElements();
    // Signed in: also syncs to the account (see lib/account-sync.js). Signed out, or with
    // the feature off, this is a pure passthrough to the same localStorage wrapper.
    this.snapshotStore = syncedStore(TOOL_NAME, store(TOOL_NAME), {
      schemaVersion: SCHEMA_VERSION,
      onStatus: mountSyncIndicator(),
    });
    this.saveTimer = null;
    this.announceTimer = null;
    this.hasEngaged = false;
    this.hasFiredResult = false;
    this.dock = initToolDock({
      dock: byId('snapshot-dock'),
      jumpTarget: byId('snapshot-readout'),
      autoVisibility: false,
      hideJumpUntilComplete: true,
    });

    this.state = this.loadInitialState();
    this.applyStateToForm();
    this.renderProgress();
    this.renderReadoutIfComplete();
    this.wireEvents();
    trackToolAction(TOOL_NAME, 'open');
  }

  cacheElements() {
    this.form = byId('snapshot-form');
    this.els = {
      progressText: byId('snapshot-progress-text'),
      startOver: byId('snapshot-start-over'),
      readoutSection: byId('snapshot-readout'),
      readoutIncomplete: byId('snapshot-readout-incomplete'),
      domainReadouts: byId('snapshot-domain-readouts'),
      overlayName: byId('snapshot-overlay-name'),
      overlayDescription: byId('snapshot-overlay-description'),
      overlayWeakSignal: byId('snapshot-overlay-weak-signal'),
      overlayAnnouncer: byId('snapshot-overlay-announcer'),
    };
  }

  loadInitialState() {
    const saved = this.snapshotStore.load();
    if (!saved) return createInitialState();

    const result = parseSavedState(saved);
    if (result.status === 'ok' || result.status === 'migrated') return result.state;
    // 'malformed' or 'unsupported-newer': start fresh rather than surface a
    // recovery bar for a short quiz - low enough stakes that a silent reset
    // is kinder than a modal, unlike the charter builder's document-length stakes.
    return createInitialState();
  }

  applyStateToForm() {
    for (const item of ITEMS) {
      const value = this.state.answers[item.id];
      if (!value) continue;
      const input = this.form.querySelector(`input[data-snapshot-item="${item.id}"][value="${value}"]`);
      if (input) input.checked = true;
    }
  }

  wireEvents() {
    this.form.addEventListener('change', (e) => this.handleAnswerChange(e));
    this.els.startOver.addEventListener('click', () => this.handleStartOver());
  }

  handleAnswerChange(e) {
    const el = e.target.closest('input[data-snapshot-item]');
    if (!el || !this.form.contains(el)) return;
    const itemId = el.dataset.snapshotItem;
    const value = Number(el.value);
    this.state = setAnswer(this.state, itemId, value);
    this.markEngaged();
    this.renderProgress();
    this.renderReadoutIfComplete();
    this.scheduleAutosave();
  }

  markEngaged() {
    if (this.hasEngaged) return;
    this.hasEngaged = true;
    trackToolAction(TOOL_NAME, 'engage');
    this.dock?.show();
  }

  renderProgress() {
    const answered = countAnswered(this.state);
    this.els.progressText.textContent = `${answered} of ${TOTAL_ITEMS} answered`;
    this.dock?.setReading(`${answered} of ${TOTAL_ITEMS} answered`);
  }

  renderReadoutIfComplete() {
    const readout = buildReadout(this.state);
    this.dock?.setComplete(!!readout);
    if (!readout) {
      this.els.readoutSection.hidden = true;
      this.els.readoutIncomplete.hidden = false;
      if (this.announceTimer) clearTimeout(this.announceTimer);
      this.els.overlayAnnouncer.textContent = '';
      return;
    }

    this.els.readoutIncomplete.hidden = true;
    this.els.readoutSection.hidden = false;

    clearChildren(this.els.domainReadouts);
    for (const domainKey of DOMAIN_ORDER) {
      const d = readout.domains.find((x) => x.domain === domainKey);
      this.els.domainReadouts.appendChild(this.buildDomainCard(d));
    }

    this.els.overlayName.textContent = readout.overlay.name;
    this.els.overlayDescription.textContent = readout.overlay.description;
    this.els.overlayWeakSignal.hidden = !readout.overlay.weakSignal;
    this.els.overlayWeakSignal.textContent = readout.overlay.weakSignalText ?? '';
    const weakSignalSuffix = readout.overlay.weakSignalText ? ` ${readout.overlay.weakSignalText}` : '';
    this.scheduleAnnouncement(`Your care/accountability pattern: ${readout.overlay.name}. ${readout.overlay.description}${weakSignalSuffix}`);

    if (!this.hasFiredResult) {
      this.hasFiredResult = true;
      // Coarse, non-identifying param only - never raw item-level answers.
      trackToolAction(TOOL_NAME, 'result', { overlay_pattern: readout.overlay.name });
    }
  }

  buildDomainCard(domain) {
    const card = document.createElement('div');
    card.className = 'tool-snapshot-domain-card';

    const title = document.createElement('h3');
    title.className = 'tool-snapshot-domain-title';
    title.textContent = domain.label;
    card.appendChild(title);

    if (domain.uniform) {
      const p = document.createElement('p');
      p.textContent = domain.uniformText;
      card.appendChild(p);
      return card;
    }

    if (domain.lowFloor) {
      const floor = document.createElement('p');
      floor.className = 'tool-snapshot-domain-floor';
      floor.textContent = domain.lowFloorText;
      card.appendChild(floor);
    } else {
      const strength = document.createElement('p');
      strength.className = 'tool-snapshot-domain-strength';
      strength.textContent = domain.strengthText;
      card.appendChild(strength);
    }

    const growth = document.createElement('p');
    growth.className = 'tool-snapshot-domain-growth';
    growth.textContent = domain.growthText;
    card.appendChild(growth);

    // Disclosed rather than hidden: the named item is only one of several at
    // its level - see TIE_NOTE_TEXT in lib/snapshot-model.js.
    if (domain.tieNoteText) {
      const tie = document.createElement('p');
      tie.className = 'tool-snapshot-domain-tie';
      tie.textContent = domain.tieNoteText;
      card.appendChild(tie);
    }

    return card;
  }

  /**
   * Mirrors roi.js's scheduleAnnouncement: the visible overlay name/description
   * update immediately, but a screen reader announcing every intermediate
   * change while someone revises earlier answers would be noisy. This mirrors
   * the same text into a separate visually-hidden aria-live region, debounced.
   */
  scheduleAnnouncement(text) {
    if (this.announceTimer) clearTimeout(this.announceTimer);
    this.announceTimer = setTimeout(() => {
      this.announceTimer = null;
      if (this.els.overlayAnnouncer.textContent !== text) {
        this.els.overlayAnnouncer.textContent = text;
      }
    }, ANNOUNCE_DEBOUNCE_MS);
  }

  scheduleAutosave() {
    if (this.saveTimer) clearTimeout(this.saveTimer);
    this.saveTimer = setTimeout(() => this.autosave(), AUTOSAVE_DEBOUNCE_MS);
  }

  autosave() {
    const toSave = { ...this.state, savedAt: Date.now() };
    const json = serializeForSave(toSave);
    if (!json) return; // over budget - keep whatever was previously saved
    this.snapshotStore.save(toSave);
  }

  handleStartOver() {
    this.snapshotStore.deleteAccountState();
    this.state = createInitialState();
    this.hasFiredResult = false;
    this.form.reset();
    this.renderProgress();
    this.renderReadoutIfComplete();
  }
}

document.addEventListener('DOMContentLoaded', async () => {
  if (!byId('snapshot-form')) return;
  // Reconcile with the account BEFORE the tool reads its own storage, so it simply loads
  // whatever won. A no-op (and no request) when signed out, and bounded by its own short
  // timeout so a slow network delays startup rather than blocking it.
  await hydrate(TOOL_NAME, store(TOOL_NAME), SCHEMA_VERSION);
  new LeadershipPracticeSnapshot();
});
